import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getDashboardStats, DashboardStats,
  getPlayers, getSubscriptions, getMemberships, Subscription,
  getAllContent,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import './Dashboard.css';

function gbp(pence: number): string {
  const v = (Number(pence) || 0) / 100;
  return '£' + v.toLocaleString('en-GB', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });
}

function fmtDate(s: string): string {
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Lightweight SVG donut (no chart lib) for the status mix. */
function Donut({ segments, centerTop, centerBottom }: {
  segments: { value: number; color: string }[];
  centerTop: string;
  centerBottom: string;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const R = 54, SW = 16, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg width="120" height="120" viewBox="0 0 132 132" role="img" aria-label="Collections status breakdown">
      <circle cx="66" cy="66" r={R} fill="none" stroke="#eef2f7" strokeWidth={SW} />
      {total > 0 && segments.filter(s => s.value > 0).map((s, i) => {
        const len = (s.value / total) * C;
        const seg = (
          <circle key={i} cx="66" cy="66" r={R} fill="none" stroke={s.color} strokeWidth={SW}
            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform="rotate(-90 66 66)" />
        );
        offset += len;
        return seg;
      })}
      <text x="66" y="62" textAnchor="middle" fontSize="28" fontWeight="800" fill="#111827">{centerTop}</text>
      <text x="66" y="82" textAnchor="middle" fontSize="11" fill="#6b7280">{centerBottom}</text>
    </svg>
  );
}

function Dashboard() {
  const { features, plan } = useAuth();
  const collectionsEnabled = features?.collections === true;
  const newsEnabled = plan === 'club' || plan === 'pro';

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [playerCount, setPlayerCount] = useState<number | null>(null);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [planCount, setPlanCount] = useState(0);
  const [lastNewsDate, setLastNewsDate] = useState<string | null | undefined>(undefined); // undefined = unchecked, null = none
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
  const clubName = authUser.clubName || 'Your Club';

  useEffect(() => { void loadStats(); }, []);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      const data = await getDashboardStats();
      setStats(data);
      // Non-blocking enrichments (tolerate gated/unavailable endpoints).
      getPlayers().then(p => setPlayerCount(p.length)).catch(() => setPlayerCount(null));
      if (collectionsEnabled) {
        Promise.all([getSubscriptions().catch(() => []), getMemberships().catch(() => [])])
          .then(([s, m]) => { setSubs(s); setPlanCount(m.length); })
          .catch(() => {});
      }
      if (newsEnabled) {
        getAllContent('published')
          .then(items => {
            const dates = items
              .filter(i => i.section.startsWith('news-'))
              .map(i => String((i.content as Record<string, any>)?.date || ''))
              .filter(Boolean)
              .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
            setLastNewsDate(dates[0] ?? null);
          })
          .catch(() => setLastNewsDate(undefined));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard stats');
    } finally {
      setIsLoading(false);
    }
  };

  const col = useMemo(() => {
    const by = (st: string) => subs.filter(s => s.status === st).length;
    const live = subs.filter(s => s.status !== 'cancelled');
    return {
      active: by('active'), pending: by('pending'), pastDue: by('past_due'),
      paused: by('paused'), completed: by('completed'),
      players: new Set(live.map(s => s.playerId).filter(Boolean)).size,
      payers: new Set(live.map(s => s.payerId).filter(Boolean)).size,
      total: subs.length,
    };
  }, [subs]);

  const money = useMemo(() => {
    let booked = 0, collected = 0;
    for (const s of subs) {
      const amt = Number(s.amount) || 0;
      // A fee is paid or it isn't. The old maths multiplied an amount by
      // installmentsPaid, which is why a paid one-off once showed as
      // "Completed · 1" next to "£0 collected so far".
      if (s.status === 'completed') collected += amt;
      if (s.status !== 'cancelled') booked += amt;
    }
    return { booked, collected, outstanding: Math.max(0, booked - collected) };
  }, [subs]);

  if (isLoading) return <div className="loading">Loading dashboard...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!stats) return <div className="error-message">No data available</div>;

  const SEGMENTS: { key: string; label: string; n: number; color: string }[] = [
    { key: 'active', label: 'Active', n: col.active, color: '#22c55e' },
    { key: 'pending', label: 'Not paid', n: col.pending, color: '#f59e0b' },
    { key: 'past_due', label: 'Past due', n: col.pastDue, color: '#ef4444' },
    { key: 'paused', label: 'Paused', n: col.paused, color: '#94a3b8' },
    { key: 'completed', label: 'Completed', n: col.completed, color: '#3b82f6' },
  ];

  const staleNews = !!lastNewsDate && (Date.now() - new Date(lastNewsDate).getTime()) > 30 * 24 * 3600 * 1000;

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Welcome to {clubName} Admin</p>
      </div>

      {newsEnabled && lastNewsDate !== undefined && (
        <div className={`news-nudge${staleNews || lastNewsDate === null ? ' is-stale' : ''}`}>
          <span className="news-nudge-icon"><img src="/icons/news.png" alt="" aria-hidden="true" /></span>
          <span className="news-nudge-text">
            {lastNewsDate
              ? <>Your last news update was <strong>{fmtDate(lastNewsDate)}</strong>{staleNews ? ', anything new to share with members?' : '.'}</>
              : <>You haven't posted any club news yet.</>}
          </span>
          <Link to="/news" className="news-nudge-cta">{lastNewsDate ? 'Post an update →' : 'Write your first post →'}</Link>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><img src="/icons/clubmngt.png" alt="" aria-hidden="true" /></div>
          <div className="stat-content">
            <p className="stat-label">Teams</p>
            <p className="stat-value">{stats.teamCount}</p>
          </div>
          <Link to="/teams" className="stat-link">Manage teams &rarr;</Link>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><img src="/icons/players.png" alt="" aria-hidden="true" /></div>
          <div className="stat-content">
            <p className="stat-label">Players</p>
            <p className="stat-value">{playerCount ?? '-'}</p>
          </div>
          <Link to="/teams" className="stat-link">View roster &rarr;</Link>
        </div>

        {collectionsEnabled ? (
          <div className="stat-card stat-card-highlight">
            <div className="stat-icon"><img src="/icons/collections.png" alt="" aria-hidden="true" /></div>
            <div className="stat-content">
              <p className="stat-label">Active collections</p>
              <p className="stat-value">{col.active}</p>
            </div>
            <Link to="/collections/subscriptions" className="stat-link">Manage collections &rarr;</Link>
          </div>
        ) : (
          <div className="stat-card">
            <div className="stat-icon"><img src="/icons/inquries.png" alt="" aria-hidden="true" /></div>
            <div className="stat-content">
              <p className="stat-label">Inquiries</p>
              <p className="stat-value">{stats.inquiryCount}</p>
            </div>
          </div>
        )}
      </div>

      {collectionsEnabled && (
        <div className="col-dash">
          <div className="col-dash-head">
            <h2 className="section-title">Collections</h2>
            <Link to="/collections" className="stat-link">Open Collections &rarr;</Link>
          </div>

          {col.total === 0 ? (
            <div className="empty-state" style={{ padding: 28 }}>
              <p>No fees yet. Create a plan and enrol players to start collecting.</p>
              <Link to="/collections" className="ebtn ebtn-primary" style={{ marginTop: 12, display: 'inline-block' }}>Set up a plan</Link>
            </div>
          ) : (
            <>
              <div className="col-dash-row">
                <div className="col-donut">
                  <Donut
                    segments={SEGMENTS.map(s => ({ value: s.n, color: s.color }))}
                    centerTop={String(col.active)}
                    centerBottom="active"
                  />
                  <div className="col-donut-legend">
                    {SEGMENTS.filter(s => s.n > 0).map(s => (
                      <span key={s.key}><span className="col-metric-dot" style={{ background: s.color }} />{s.label} · {s.n}</span>
                    ))}
                  </div>
                </div>
                <div className="col-money-grid">
                  <div className="col-money">
                    <div className="col-money-num">{gbp(money.booked)}</div>
                    <div className="col-money-label">Active book value</div>
                  </div>
                  <div className="col-money">
                    <div className="col-money-num">{gbp(money.collected)}</div>
                    <div className="col-money-label">Collected so far</div>
                  </div>
                  <div className="col-money" title="Fees sent out or waiting to be sent, that nobody has paid yet.">
                    <div className="col-money-num">{gbp(money.outstanding)}</div>
                    <div className="col-money-label">Still owed</div>
                  </div>
                </div>
              </div>

              <div className="col-dash-grid" style={{ marginTop: 16 }}>
                <div className="col-metric"><div className="col-metric-num">{col.players}</div><div className="col-metric-label">Players enrolled</div></div>
                <div className="col-metric"><div className="col-metric-num">{col.payers}</div><div className="col-metric-label">Payers</div></div>
                <div className="col-metric"><div className="col-metric-num">{planCount}</div><div className="col-metric-label">Plans</div></div>
                <div className="col-metric"><div className="col-metric-num">{col.pending}</div><div className="col-metric-label"><span className="col-metric-dot" style={{ background: '#f59e0b' }} />Not paid</div></div>
                <div className="col-metric"><div className="col-metric-num">{col.pastDue}</div><div className="col-metric-label"><span className="col-metric-dot" style={{ background: '#ef4444' }} />Past due</div></div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="dashboard-sections">
        <div className="dashboard-section">
          <h2 className="section-title">Quick Actions</h2>
          <div className="quick-actions">
            <Link to="/content" className="action-card">
              <span className="action-icon"><img src="/icons/website.png" alt="" aria-hidden="true" /></span>
              <div className="action-content">
                <h3 className="action-title">Edit Content</h3>
                <p className="action-description">Update website content and pages</p>
              </div>
            </Link>

            <Link to="/teams" className="action-card">
              <span className="action-icon"><img src="/icons/clubmngt.png" alt="" aria-hidden="true" /></span>
              <div className="action-content">
                <h3 className="action-title">Manage Teams</h3>
                <p className="action-description">Add, edit or remove teams</p>
              </div>
            </Link>

            {collectionsEnabled && (
              <Link to="/collections" className="action-card">
                <span className="action-icon"><img src="/icons/collections.png" alt="" aria-hidden="true" /></span>
                <div className="action-content">
                  <h3 className="action-title">Collections</h3>
                  <p className="action-description">Plans, enrolments and payers</p>
                </div>
              </Link>
            )}

            <Link to="/settings" className="action-card">
              <span className="action-icon"><img src="/icons/settings.png" alt="" aria-hidden="true" /></span>
              <div className="action-content">
                <h3 className="action-title">Site Settings</h3>
                <p className="action-description">Configure site preferences</p>
              </div>
            </Link>

            {plan === 'free' && (
              <Link to="/upgrade" className="action-card">
                <span className="action-icon"><img src="/icons/upgrade.png" alt="" aria-hidden="true" /></span>
                <div className="action-content">
                  <h3 className="action-title">Upgrade Plan</h3>
                  <p className="action-description">Unlock shop, news and more</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
